import { useState, useMemo, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/component/navbar';
import OperateAside from '@/components/OperateAside';
import { ensureOperateOwner } from '@/utils/operateStorage';

const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 1989 }, (_, i) => String(currentYear - i));
const emirates = ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah'];
const entityTypes = ['LLC', 'FZ-LLC', 'Sole Proprietorship', 'Branch Office', 'Free Zone Establishment'];

const WEBSITE_RE = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[^\s]*)?$/i;

// ── Reusable field shell with label, icon, error message ──────────────────────
function Field({ label, error, icon, delay, children }: {
  label: string; error?: string; icon?: ReactNode; delay: number; children: ReactNode;
}) {
  return (
    <div style={{ animation: `opRise .5s ${delay}s both` }}>
      <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">{label}</label>
      <div className="relative">
        {icon && <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">{icon}</span>}
        {children}
      </div>
      {error && (
        <p className="flex items-center gap-1 text-[12px] font-medium text-red-500 mt-1.5" style={{ animation: 'opFade .25s ease both' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {error}
        </p>
      )}
    </div>
  );
}

const ICONS = {
  building: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4M9 6h.01M15 6h.01M9 10h.01M15 10h.01M9 14h.01M15 14h.01"/></svg>,
  globe: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20"/></svg>,
  hash: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>,
};

export default function OperateCompanyInfo() {
  // Reset stored answers if the logged-in account changed (per-account isolation).
  ensureOperateOwner();
  // The operate flow is an accounting onboarding — keep the active view on accounting
  // so the logo and the post-onboarding redirect land on the accounting dashboard
  // (even if the user previously activated consulting).
  useEffect(() => { localStorage.setItem('activeServiceView', 'accounting'); }, []);
  const [companyName, setCompanyName] = useState(() => localStorage.getItem('operateCompanyName') || '');
  const [website, setWebsite] = useState(() => localStorage.getItem('operateWebsite') || '');
  const [formationYear, setFormationYear] = useState(() => localStorage.getItem('operateFormationYear') || '');
  const [formationEmirate, setFormationEmirate] = useState(() => localStorage.getItem('operateFormationEmirate') || '');
  const [entityType, setEntityType] = useState(() => localStorage.getItem('operateEntityType') || '');
  const [taxNumber, setTaxNumber] = useState(() => localStorage.getItem('operateTaxNumber') || '');
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (!companyName.trim()) e.companyName = 'Company name is required';
    else if (companyName.trim().length < 2) e.companyName = 'Enter a valid company name';
    if (website.trim() && !WEBSITE_RE.test(website.trim())) e.website = 'Enter a valid website (e.g. company.com)';
    if (!formationYear) e.formationYear = 'Select a formation year';
    if (!formationEmirate) e.formationEmirate = 'Select a formation emirate';
    if (!entityType) e.entityType = 'Select an entity type';
    if (taxNumber.trim() && !/^\d{15}$/.test(taxNumber.replace(/\s/g, ''))) e.taxNumber = 'TRN must be 15 digits';
    return e;
  }, [companyName, website, formationYear, formationEmirate, entityType, taxNumber]);

  const show = (k: string) => (submitted || touched[k]) ? errors[k] : undefined;
  const blur = (k: string) => setTouched(t => ({ ...t, [k]: true }));

  const handleNext = () => {
    setSubmitted(true);
    if (Object.keys(errors).length) {
      const first = document.querySelector('[data-invalid="true"]') as HTMLElement | null;
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    localStorage.setItem('operateCompanyName', companyName.trim());
    localStorage.setItem('operateWebsite', website.trim());
    localStorage.setItem('operateFormationYear', formationYear);
    localStorage.setItem('operateFormationEmirate', formationEmirate);
    localStorage.setItem('operateEntityType', entityType);
    localStorage.setItem('operateTaxNumber', taxNumber.trim());
    navigate('/operate-company-doing');
  };

  const inputCls = (k: string, hasIcon = true) =>
    `w-full ${hasIcon ? 'pl-10' : 'px-4'} pr-4 py-3 border rounded-xl text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 transition-all ${
      show(k)
        ? 'border-red-400 focus:ring-red-200 focus:border-red-400'
        : 'border-gray-300 focus:ring-amber-400/40 focus:border-amber-400 hover:border-gray-400'
    }`;
  const selCls = (k: string) => `${inputCls(k, true)} cursor-pointer appearance-none`;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <style>{`
        @keyframes opRise { from { opacity:0; transform: translateY(16px); } to { opacity:1; transform:none; } }
        @keyframes opFade { from { opacity:0; } to { opacity:1; } }
        @keyframes opFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-9px); } }
        @keyframes opBarGrow { from { width:0; } to { width:50%; } }
      `}</style>
      <Navbar hideSignBtn={true} logoTo="/dashboard" />
      <div className="flex flex-1">
        <div className="hidden md:block w-1/2 relative">
          <OperateAside />
        </div>

        <div className="w-full md:w-1/2 flex flex-col justify-center px-8 lg:px-16 py-12 overflow-y-auto">
          <div className="w-full max-w-md mx-auto">
            {/* Progress */}
            <div style={{ animation: 'opFade .5s both' }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-amber-600">Step 1 of 2</span>
                <span className="text-[11px] font-semibold text-gray-400">Company details</span>
              </div>
              <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden mb-7">
                <div className="h-full rounded-full" style={{ background: 'linear-gradient(90deg,#FFD54F,#E6B400)', animation: 'opBarGrow .8s cubic-bezier(.4,0,.2,1) both' }} />
              </div>
              <h1 className="text-[26px] font-extrabold text-gray-900 mb-1.5 leading-tight">What's your company's information?</h1>
              <p className="text-sm text-gray-500 mb-7">Tell us about your entity so we set up your books, VAT and tax filings correctly.</p>
            </div>

            <div className="space-y-4">
              <div data-invalid={!!show('companyName')}>
                <Field label="Company name" error={show('companyName')} icon={ICONS.building} delay={0.08}>
                  <input type="text" placeholder="e.g. Conncct Technologies LLC" value={companyName}
                    onChange={e => setCompanyName(e.target.value)} onBlur={() => blur('companyName')} className={inputCls('companyName')} />
                </Field>
              </div>

              <div data-invalid={!!show('website')}>
                <Field label="Website" error={show('website')} icon={ICONS.globe} delay={0.14}>
                  <input type="text" placeholder="company.com" value={website}
                    onChange={e => setWebsite(e.target.value)} onBlur={() => blur('website')} className={inputCls('website')} />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div data-invalid={!!show('formationYear')}>
                  <Field label="Formation year" error={show('formationYear')} delay={0.2}>
                    <select value={formationYear} onChange={e => setFormationYear(e.target.value)} onBlur={() => blur('formationYear')} className={selCls('formationYear').replace('pl-10', 'pl-4')}>
                      <option value="">Select year</option>
                      {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
                  </Field>
                </div>
                <div data-invalid={!!show('formationEmirate')}>
                  <Field label="Formation emirate" error={show('formationEmirate')} delay={0.26}>
                    <select value={formationEmirate} onChange={e => setFormationEmirate(e.target.value)} onBlur={() => blur('formationEmirate')} className={selCls('formationEmirate').replace('pl-10', 'pl-4')}>
                      <option value="">Select</option>
                      {emirates.map(em => <option key={em} value={em}>{em}</option>)}
                    </select>
                    <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
                  </Field>
                </div>
              </div>

              <div data-invalid={!!show('entityType')}>
                <Field label="Entity type" error={show('entityType')} delay={0.32}>
                  <select value={entityType} onChange={e => setEntityType(e.target.value)} onBlur={() => blur('entityType')} className={selCls('entityType').replace('pl-10', 'pl-4')}>
                    <option value="">Select entity type</option>
                    {entityTypes.map(et => <option key={et} value={et}>{et}</option>)}
                  </select>
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
                </Field>
              </div>

              <div data-invalid={!!show('taxNumber')}>
                <Field label="Tax registration number (TRN)" error={show('taxNumber')} icon={ICONS.hash} delay={0.38}>
                  <input type="text" inputMode="numeric" placeholder="15-digit TRN (optional)" value={taxNumber}
                    onChange={e => setTaxNumber(e.target.value.replace(/[^\d\s]/g, ''))} onBlur={() => blur('taxNumber')} className={inputCls('taxNumber')} />
                </Field>
              </div>
            </div>

            <div className="flex items-center gap-3 mt-8" style={{ animation: 'opRise .5s .44s both' }}>
              <button type="button" onClick={() => navigate(-1)}
                className="px-7 py-3 border-2 border-gray-200 rounded-xl font-semibold text-gray-600 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors">
                ← Back
              </button>
              <button type="button" onClick={handleNext}
                className="flex-1 px-7 py-3 rounded-xl font-bold text-[#17263A] transition-transform hover:-translate-y-0.5 active:translate-y-0 shadow-md"
                style={{ background: 'linear-gradient(135deg,#FFD54F,#E6B400)', boxShadow: '0 6px 18px rgba(230,180,0,.32)' }}>
                Continue →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
